import {
  ExclamationTriangleIcon,
  InformationCircleIcon,
  ShieldExclamationIcon,
} from '@heroicons/react/24/outline';
import type { ReactNode } from 'react';

export type CalloutTone = 'info' | 'warning' | 'danger' | 'success' | 'neutral';

const TONES: Record<CalloutTone, string> = {
  info: 'bg-info-bg text-info-fg border-info-border',
  warning: 'bg-warning-bg text-warning-fg border-warning-border',
  danger: 'bg-danger-bg text-danger-fg border-danger-border',
  success: 'bg-success-bg text-success-fg border-success-border',
  neutral: 'bg-neutral-bg text-neutral-fg border-neutral-border',
};

const ICONS: Record<CalloutTone, typeof InformationCircleIcon> = {
  info: InformationCircleIcon,
  warning: ExclamationTriangleIcon,
  danger: ShieldExclamationIcon,
  success: InformationCircleIcon,
  neutral: InformationCircleIcon,
};

interface CalloutProps {
  tone?: CalloutTone;
  children: ReactNode;
  action?: ReactNode;
  /** `alert` for anything a responder must act on; the default is passive. */
  role?: 'alert' | 'status' | 'note';
}

export function Callout({ tone = 'info', children, action, role = 'note' }: CalloutProps) {
  const Icon = ICONS[tone];
  return (
    <div
      role={role === 'note' ? undefined : role}
      className={`flex items-start gap-2.5 rounded-lg border px-4 py-3 text-sm ${TONES[tone]}`}
    >
      <Icon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
      <div className="min-w-0 flex-1">{children}</div>
      {action ? <div className="flex shrink-0 items-center gap-2">{action}</div> : null}
    </div>
  );
}
